/* eslint-disable no-underscore-dangle */
import { Loader } from '@fountain/fountain-ui-components';
import {
  Action,
  CancelablePromise,
  Condition,
  DistributeRule,
  DocumentSigningStagesRuleAttributes,
  DocumentSigningStagesTemplateSetting,
  FunnelDetails,
  OfferLetterTemplatesStage,
  PartnerRuleAttributes,
  ReasonSetting,
  ReminderSetting,
  RuleAttributes,
  SidebarStage,
  StageType,
  StageUpdateParams,
  TechCheckStageSetting,
  TriggerSetting,
  VideoRecordingStageQuestion,
  WorkflowCustomStage,
  WorkflowEditorService,
  WorkflowStageDetail,
  WorkflowStageUpdates,
} from 'api-clients/monolith';
import _isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { addMessageAction } from 'containers/FlashMessage/actions';
import {
  DEFAULT_ASSESSMENT_RULE,
  DEFAULT_ELSE_ACTION,
  DEFAULT_RULE,
} from 'containers/WorkflowEditor/components/Rules/constants';
import { RulesProps } from 'containers/WorkflowEditor/components/Rules/types';
import { useApiServiceMutation } from 'hooks/useApiServiceMutation';
import { addWorkflowInvalidatedListener } from 'utils/appEvents';

import { DocumentSigningRuleProps } from '../components/Rules/DocumentSigningRules/types';
import { PartnerRuleProps } from '../components/Rules/PartnerRules/types';
import { SingleRuleStageTypes } from '../components/Rules/RuleCard/RuleCard.fixtures';
import { UnsavedChangesPrompt } from '../components/UnsavedChangesPrompt';
import { DEFAULT_DISTRIBUTE_RULE } from '../components/WorkflowEditorContent/StageDetail/DistributeApplicantsRuleStage/DistributeApplicantsRuleStage';
import { StagesSidebar } from '../components/WorkflowEditorContent/StagesSidebar';
import { StageContext } from '../contexts/stageContext';
import { useStage } from '../hooks/useStage';
import messages from './messages';

export const transformRules = (
  rules: (
    | RuleAttributes
    | PartnerRuleAttributes
    | DocumentSigningStagesRuleAttributes
  )[] = [],
  stageType: StageType = 'RuleStage',
): RulesProps | PartnerRuleProps | DocumentSigningRuleProps => {
  const rulesHash: RulesProps | PartnerRuleProps | DocumentSigningRuleProps =
    {};
  let initialRules: (
    | RuleAttributes
    | PartnerRuleAttributes
    | DocumentSigningStagesRuleAttributes
  )[] = rules;
  if (rules.length === 0) {
    if (
      stageType === 'PartnerStage' ||
      stageType === 'DocumentSignatureStage' ||
      stageType === 'DocumentSigningStage' ||
      stageType === 'ComplianceStage' ||
      stageType === 'RehireCheckStage'
    ) {
      initialRules = [];
    } else if (stageType === 'AssessmentStage') {
      initialRules = [DEFAULT_ASSESSMENT_RULE] as RuleAttributes[];
    } else if (!SingleRuleStageTypes.includes(stageType)) {
      initialRules = [DEFAULT_RULE] as RuleAttributes[];
    }
  }

  initialRules.forEach((rule, index) => {
    // TODO: Have backend send uuids for existing rules rather than set new ones for all.
    // Initialize position by array index so order is carried in data, not object-key insertion order
    // (JS re-sorts integer-shaped keys numerically, making insertion order unreliable for saved rules).
    rulesHash[rule.id ?? uuid()] = { ...rule, position: index + 1 };
  });
  return rulesHash;
};

type PartnerRuleChild = {
  id?: string | number;
  action_set_id?: number;
  action_set_type?: string;
  partner_stage_rule_id?: number | null;
  ref_funnel_1_id?: number | null;
  _destroy?: boolean;
};

// Build the nested attributes for a slot rule's has_one child (action or
// condition) during a partner-rule reorder swap: the child carries the
// content rule's behavior but keeps the slot child's row identity.
// Parent-linkage fields (id, action_set_id/action_set_type on actions,
// partner_stage_rule_id on conditions) must stay with the slot — sending the
// content rule's values would re-parent the content row instead of updating
// the slot's. `ref_funnel_1_id` is serialized by the backend but not
// permitted in partner rule params, so it is stripped rather than sent.
export const swapPartnerRuleChild = <T extends PartnerRuleChild>(
  slotChild: T | null | undefined,
  contentChild: T | null | undefined,
): Record<string, unknown> | undefined => {
  if (!contentChild) {
    // Nothing should occupy this slot: destroy the slot's existing child.
    return typeof slotChild?.id === 'number'
      ? { id: slotChild.id, _destroy: true }
      : undefined;
  }

  const {
    id: _id,
    action_set_id: _actionSetId,
    action_set_type: _actionSetType,
    partner_stage_rule_id: _partnerStageRuleId,
    ref_funnel_1_id: _refFunnel1Id,
    _destroy: _contentDestroy,
    ...contentRest
  } = contentChild;

  if (!slotChild) {
    // The slot rule has no child of this kind yet: create one from content.
    return contentRest;
  }

  return {
    ...contentRest,
    ...(typeof slotChild.id === 'number' && { id: slotChild.id }),
    ...(slotChild.action_set_id !== undefined && {
      action_set_id: slotChild.action_set_id,
    }),
    ...(slotChild.action_set_type !== undefined && {
      action_set_type: slotChild.action_set_type,
    }),
    ...(slotChild.partner_stage_rule_id !== undefined && {
      partner_stage_rule_id: slotChild.partner_stage_rule_id,
    }),
  };
};

type BaseProps = {
  externalId: string;
  funnelStages: SidebarStage[];
  withSidebar: false;
  opening: FunnelDetails;
};

type WithSidebarProps = {
  externalId: string;
  funnelStages: SidebarStage[];
  unsupportedAxStages: string[];
  opening: FunnelDetails;
  refetchStages: () => void;
  selectedStage: SidebarStage | undefined;
  withSidebar: true;
};

type AccountParams = {
  accountSlug: string;
};

export type StageContextProviderProps = BaseProps | WithSidebarProps;

export const StageContextProvider: React.FC<
  StageContextProviderProps
> = props => {
  const { children, externalId, funnelStages, withSidebar, opening } = props;
  const dispatch = useDispatch();
  const intl = useIntl();

  const { result: stageResult, refetch: refetchStage } = useStage({
    funnelSlug: opening.slug,
    externalId,
  });

  const [stage, setStage] = useState<WorkflowStageDetail>(
    {} as WorkflowStageDetail,
  );
  const [isDirty, setIsDirty] = useState(false);
  const [isStageTitleDirty, setIsStageTitleDirty] = useState(false);
  const [canSave, setCanSave] = useState(true);
  const [rules, setRules] = useState<RulesProps>({});
  const [partnerRules, setPartnerRules] = useState<PartnerRuleProps>({});
  const [documentSigningRules, setDocumentSigningRules] =
    useState<DocumentSigningRuleProps>({});
  const { accountSlug } = useParams<AccountParams>();
  const history = useHistory();

  const updateStageSuccessCallback = () => {
    dispatch(
      addMessageAction(
        intl.formatMessage(messages.changesSavedSuccessfully),
        'success',
      ),
    );
    setIsDirty(false);

    if (withSidebar) {
      props.refetchStages();
    }
  };

  const updateStageErrorCallback = () => {
    dispatch(
      addMessageAction(
        intl.formatMessage(messages.changesFailedToSave),
        'error',
      ),
    );
    setIsDirty(true);

    if (withSidebar) {
      props.refetchStages();
    }
  };

  const {
    result: updateStageResult,
    reset: resetUpdateStageResult,
    mutation: updateStage,
  } = useApiServiceMutation<
    WorkflowStageDetail,
    (
      stageExternalId: string,
      funnelSlug: string,
      requestBody?: WorkflowStageUpdates,
    ) => CancelablePromise<WorkflowStageDetail>,
    { errors: Record<string, Array<string>> }
  >(
    // eslint-disable-next-line @typescript-eslint/unbound-method
    WorkflowEditorService.patchInternalApiWorkflowEditorFunnelsStages,
    {
      onSuccess: updateStageSuccessCallback,
      onError: updateStageErrorCallback,
    },
  );

  const setStageAndTransformationStates = (stage: WorkflowStageDetail) => {
    const stageCopy = { ...stage };

    // If the stage is a RuleStage and it doesn't have else_action_set_attributes set yet,
    // we want to manually set that here so the ElseAction component can properly update it
    if (
      stageCopy.type === 'RuleStage' &&
      !stageCopy.additional_info.else_action_set_attributes
    ) {
      stageCopy.additional_info.else_action_set_attributes = {
        actions_attributes: [{ ...DEFAULT_ELSE_ACTION }],
      };
    }

    // If stage is DistributeApplicantsRuleStage and doesn't have any distribute_rules one is created by default
    if (
      stageCopy.type === 'DistributeApplicantsRuleStage' &&
      stageCopy.additional_info.distribute_rules?.length === 0 &&
      stageCopy.additional_info.default_location
    ) {
      stageCopy.additional_info.distribute_rules = [
        {
          ...DEFAULT_DISTRIBUTE_RULE,
          id: uuid(),
          percentage: 100,
          to_location_id: stageCopy.additional_info.default_location.id,
        },
      ];
    }

    // Batch all state updates so they apply in a single render cycle.
    // Without batching (React 16 does not auto-batch setState calls from
    // effects), the dirty-check useEffect can fire between setStage and
    // setRules/setPartnerRules/setDocumentSigningRules, comparing the new
    // stage's rule_set_attributes against the stale (previous stage's) rules
    // state and producing a false isDirty = true.
    unstable_batchedUpdates(() => {
      setStage(stageCopy);

      // Reset the save-blocking veto on every stage load - only the loaded stage's own
      // settings panel (e.g. RehireCheckStage with zero connectors) gets to set this back to
      // false, via its own effect once its data (e.g. the connector list) resolves.
      setCanSave(true);

      if (stageCopy.type === 'PartnerStage') {
        const stageDataRules =
          stageCopy.additional_info.rule_set_attributes?.rules_attributes;

        setPartnerRules(
          transformRules(stageDataRules, stageCopy.type) as PartnerRuleProps,
        );
      } else if (
        stageCopy.type === 'DocumentSignatureStage' ||
        stageCopy.type === 'DocumentSigningStage'
      ) {
        const stageDataRules =
          stageCopy.additional_info.template_rule_set_attributes
            ?.rules_attributes;

        setDocumentSigningRules(
          transformRules(
            stageDataRules,
            stageCopy.type,
          ) as DocumentSigningRuleProps,
        );
      } else if (
        stageCopy.type === 'RuleStage' ||
        stageCopy.type === 'CustomStage' ||
        stageCopy.type === 'AssessmentStage' ||
        stageCopy.type === 'ComplianceStage' ||
        stageCopy.type === 'RehireCheckStage'
      ) {
        const stageDataRules =
          stageCopy.additional_info.rule_set_attributes?.rules_attributes;

        const newRules = transformRules(
          stageDataRules,
          stageCopy.type,
        ) as RulesProps;

        setRules(newRules);
      }
    });
  };

  const onDiscardChanges = useCallback(() => {
    // If we've saved changes, our fresh data is `updateStageResult`
    if (updateStageResult.status === 'ready') {
      setStageAndTransformationStates(updateStageResult.data);
      return;
    }

    // If the previous save failed, we might still have had a previously
    // successful save, which means the `stageResult` is potentially stale.
    // Refetch the stage to make sure we have up-to-date data.
    if (updateStageResult.status === 'error') {
      refetchStage();
      resetUpdateStageResult();
      return;
    }

    // Reset updateStageResult because we are working with a clean slate
    resetUpdateStageResult();

    // Reset the stage back to the server's stage data
    if (stageResult.status === 'ready') {
      setStageAndTransformationStates(stageResult.data);
    }
  }, [refetchStage, resetUpdateStageResult, stageResult, updateStageResult]);

  const generateSettingsAttributes = (
    settings:
      | ReasonSetting[]
      | TriggerSetting[]
      | ReminderSetting[]
      | undefined,
    initialSettings:
      | ReasonSetting[]
      | TriggerSetting[]
      | ReminderSetting[]
      | undefined,
  ) => {
    if (!settings || !initialSettings) {
      return undefined;
    }

    const updatedSettingIds = settings.map(s => s.id);

    const copySettings: ReasonSetting[] | TriggerSetting[] | ReminderSetting[] =
      settings.map(setting => {
        const updatedSetting = {
          ...setting,
          message_template_attributes: setting.message_template,
        };

        return updatedSetting;
      });

    initialSettings.forEach(setting => {
      if (!updatedSettingIds.includes(setting.id))
        copySettings.push({ ...setting, _destroy: true });
    });

    return copySettings;
  };

  const transformAttributes = (
    hashAttributes: Partial<{ id: number | string }>,
    shouldDeleteStringIds = true,
  ) => {
    if (!hashAttributes) {
      return null;
    }
    // If a condition or action has an id that's a string, that means
    // it's a temporary uuid so we want to delete them before we send the
    // data through the update request
    if (typeof hashAttributes.id === 'string' && shouldDeleteStringIds) {
      const copyHashAttributes = { ...hashAttributes };

      delete copyHashAttributes.id;

      return copyHashAttributes;
    }

    return hashAttributes;
  };

  const transformRulesToParams = useCallback(
    ({ shouldDeleteStringIds }: { shouldDeleteStringIds: boolean }) => {
      if (
        stage.type === 'RuleStage' ||
        stage.type === 'CustomStage' ||
        stage.type === 'AssessmentStage' ||
        stage.type === 'ComplianceStage' ||
        stage.type === 'RehireCheckStage'
      ) {
        const copyRulesAttributes = Object.values(rules).sort((a, b) => {
          if (a.position !== undefined && b.position !== undefined) {
            return a.position - b.position;
          }
          return 0;
        });

        const activeRules = copyRulesAttributes.filter(r => !r._destroy);
        const userDeletedSavedRules = copyRulesAttributes.filter(
          r => r._destroy && typeof r.id === 'number',
        );

        // Detect whether the desired order round-trips without a swap. The
        // backend has no `position` column for rules, so it always returns
        // them in ascending ID order — and any freshly created rule gets a
        // higher ID than every existing one. So the display order survives
        // save iff every new (unsaved) rule already sits after every saved
        // rule, and the saved rules' relative order is untouched. When it
        // doesn't, we work around this by swapping content (conditions +
        // actions) between rule records so that ID order == desired display
        // order after save — including a new rule's content taking over an
        // existing rule's slot when the user drags it above a saved rule.
        // Rule IDs stay stable where possible, which avoids breaking any
        // references that other records hold to those IDs.
        const savedActiveRules = activeRules.filter(
          r => typeof r.id === 'number',
        );
        const savedActiveRulesInIdOrder = [...savedActiveRules].sort(
          (a, b) => (a.id as number) - (b.id as number),
        );
        const savedCount = savedActiveRulesInIdOrder.length;
        const slotContents = activeRules.slice(0, savedCount);
        const overflowContents = activeRules.slice(savedCount);
        const trailingNewRulesOnly = slotContents.every(
          r => typeof r.id === 'number',
        );
        const savedOrderUnchanged = savedActiveRules.every(
          (rule, idx) => rule.id === savedActiveRulesInIdOrder[idx].id,
        );

        if (!trailingNewRulesOnly || !savedOrderUnchanged) {
          // Content-swap the `savedCount` existing IDs onto the
          // position-ordered content destined for those slots — that content
          // may itself come from a new (unsaved) rule the user dragged above
          // an existing one, in which case the existing row's identity is
          // kept but its content becomes the new rule's. Slot rows destroy
          // their existing child records and create new ones from the
          // position-ordered content. After save the backend returns rules
          // in ID order, which now matches the intended display order, so a
          // page reload preserves the arrangement without any frontend
          // patching.
          const swappedRules = savedActiveRulesInIdOrder.map((slotRule, i) => {
            const contentRule = slotContents[i];

            const destroyOldActions = (
              slotRule.action_set_attributes.actions_attributes ?? []
            )
              .filter(a => typeof a.id === 'number')
              .map(a => ({ id: a.id, _destroy: true }) as unknown as Action);

            const newActions = (
              contentRule.action_set_attributes.actions_attributes ?? []
            ).map(({ id: _id, ...rest }) => rest as Action);

            const destroyOldConditions = (
              slotRule.condition_set_attributes.conditions_attributes ?? []
            )
              .filter(c => typeof c.id === 'number')
              .map(c => ({ id: c.id, _destroy: true }) as unknown as Condition);

            const {
              id: _cId,
              conditions_attributes: _cAttrs,
              conditionable_id: _condableId,
              conditionable_type: _condableType,
              ref_rule_id: _refRuleId,
              ref_document_signature_template_rule_id: _refDocSigId,
              ref_document_signing_template_rule_id: _refDocSigningId,
              ...condSetRest
            } = contentRule.condition_set_attributes;

            const newConditions = (
              contentRule.condition_set_attributes.conditions_attributes ?? []
            ).map(({ id: _id, ...rest }) => rest as Condition);

            return {
              id: slotRule.id,
              action_set_attributes: {
                id: slotRule.action_set_attributes.id,
                actions_attributes: [...destroyOldActions, ...newActions],
              },
              condition_set_attributes: {
                ...condSetRest,
                id: slotRule.condition_set_attributes.id,
                conditions_attributes: [
                  ...destroyOldConditions,
                  ...newConditions,
                ],
              },
            };
          });

          const destroyUserDeleted = userDeletedSavedRules.map(r => ({
            id: r.id,
            _destroy: true as const,
          }));

          // Overflow content sits past every available existing-ID slot, all
          // of which are already spoken for by `swappedRules` above (each
          // existing row keeps its id but may now hold different content).
          // So overflow content — whether it originally belonged to a new
          // rule or to an existing row displaced by a rule that took its
          // slot — is (re)created fresh from its own content, in display
          // order, after the swapped slots; freshly created rows get the
          // highest ids and therefore keep their displayed spot at the
          // bottom of the list. All ids (rule, action_set, condition_set,
          // and their children) are stripped rather than just string ones,
          // since a displaced existing rule's content carries real numeric
          // ids that must not collide with the row `swappedRules` already
          // reassigned them to.
          const newRuleParams = overflowContents.map(ruleAttr => {
            const newActions = (
              ruleAttr.action_set_attributes.actions_attributes ?? []
            ).map(({ id: _id, ...rest }) => rest as Action);

            const {
              id: _cId,
              conditions_attributes: _cAttrs,
              conditionable_id: _condableId,
              conditionable_type: _condableType,
              ref_rule_id: _refRuleId,
              ref_document_signature_template_rule_id: _refDocSigId,
              ref_document_signing_template_rule_id: _refDocSigningId,
              ...condSetRest
            } = ruleAttr.condition_set_attributes;

            const newConditions = (
              ruleAttr.condition_set_attributes.conditions_attributes ?? []
            ).map(({ id: _id, ...rest }) => rest as Condition);

            return {
              action_set_attributes: {
                actions_attributes: newActions,
              },
              condition_set_attributes: {
                ...condSetRest,
                conditions_attributes: newConditions,
              },
            };
          });

          return {
            ...stage.additional_info?.rule_set_attributes,
            ...(swappedRules.length === 0 &&
              newRuleParams.length === 0 && { _destroy: true }),
            rules_attributes: [
              ...destroyUserDeleted,
              ...(swappedRules as unknown as RuleAttributes[]),
              ...newRuleParams,
            ],
          };
        }

        const transformedRulesAttributes: RuleAttributes[] = [];

        copyRulesAttributes.forEach(ruleAttr => {
          const { position: _pos, ...ruleAttrRest } = ruleAttr;
          transformedRulesAttributes.push({
            ...ruleAttrRest,
            action_set_attributes: {
              ...ruleAttr.action_set_attributes,
              actions_attributes:
                ruleAttr.action_set_attributes.actions_attributes?.map(
                  action =>
                    transformAttributes(
                      action,
                      shouldDeleteStringIds,
                    ) as Action,
                ),
            },
            condition_set_attributes: {
              ...ruleAttr.condition_set_attributes,
              conditions_attributes:
                ruleAttr.condition_set_attributes.conditions_attributes?.map(
                  condition =>
                    transformAttributes(
                      condition,
                      shouldDeleteStringIds,
                    ) as Condition,
                ),
            },
          });
        });

        const undestroyedRules = transformedRulesAttributes.filter(
          rule => !rule._destroy,
        );
        // Only destroy the rule set when the user actually deleted existing
        // rules (destroyed entries are still present in the transformed
        // array). A rule set the server returned with an already-empty
        // rules_attributes must round-trip unchanged, otherwise the injected
        // `_destroy: true` makes the dirty comparison fail on load.
        const shouldDestroyRuleSet =
          transformedRulesAttributes.length > 0 &&
          undestroyedRules.length === 0;

        return {
          ...stage.additional_info?.rule_set_attributes,
          ...(shouldDestroyRuleSet && { _destroy: true }),
          rules_attributes: transformedRulesAttributes,
        };
      }

      if (
        stage.type === 'DocumentSigningStage' ||
        stage.type === 'DocumentSignatureStage'
      ) {
        // Sort by `position` (server array index) so the reconstruction
        // matches the server payload order by construction — see the
        // PartnerStage branch comment below.
        const copyRulesAttributes = Object.values(documentSigningRules).sort(
          (a, b) => {
            if (a.position !== undefined && b.position !== undefined) {
              return a.position - b.position;
            }
            return 0;
          },
        );

        // Reorder handling — save only (mirrors the RuleStage content-swap
        // above). The backend has no `position` column for doc-signing rules
        // either and always returns them in ascending id order, so a user
        // reorder is persisted by destroying + recreating the child template
        // settings and conditions from the position-ordered content while
        // keeping rule/template_set/condition_set ids in place where
        // possible. This must NOT run for the dirty comparison
        // (shouldDeleteStringIds: false) — see the identical guard in the
        // PartnerStage branch below.
        if (shouldDeleteStringIds) {
          const activeRules = copyRulesAttributes.filter(r => !r._destroy);
          const userDeletedSavedRules = copyRulesAttributes.filter(
            r => r._destroy && typeof r.id === 'number',
          );
          const savedActiveRules = activeRules.filter(
            r => typeof r.id === 'number',
          );
          const savedActiveRulesInIdOrder = [...savedActiveRules].sort(
            (a, b) => (a.id as number) - (b.id as number),
          );
          const savedCount = savedActiveRulesInIdOrder.length;

          // The only ids available after save are the `savedCount` existing
          // ids (fixed, ascending) followed by freshly created ids in
          // submission order — new rows always sort after all existing ones.
          // So the desired order round-trips without any swap iff every new
          // (unsaved) rule already sits after every saved rule in the
          // display order, and the saved rules' relative order is untouched.
          const slotContents = activeRules.slice(0, savedCount);
          const overflowContents = activeRules.slice(savedCount);
          const trailingNewRulesOnly = slotContents.every(
            r => typeof r.id === 'number',
          );
          const savedOrderUnchanged = savedActiveRules.every(
            (rule, idx) => rule.id === savedActiveRulesInIdOrder[idx].id,
          );

          if (!trailingNewRulesOnly || !savedOrderUnchanged) {
            // Content-swap the `savedCount` existing ids onto the
            // position-ordered content destined for those slots — that
            // content may itself come from a new (unsaved) rule the user
            // dragged above an existing one, in which case the existing
            // row's identity is kept but its content becomes the new rule's.
            const swappedRules = savedActiveRulesInIdOrder.map(
              (slotRule, i) => {
                const contentRule = slotContents[i];

                const destroyOldTemplateSettings = (
                  slotRule.template_set_attributes
                    .template_settings_attributes ?? []
                )
                  .filter(t => typeof t.id === 'number')
                  .map(
                    t =>
                      ({
                        id: t.id,
                        _destroy: true,
                      }) as unknown as DocumentSigningStagesTemplateSetting,
                  );

                const newTemplateSettings = (
                  contentRule.template_set_attributes
                    .template_settings_attributes ?? []
                ).map(
                  ({ id: _id, ...rest }) =>
                    rest as DocumentSigningStagesTemplateSetting,
                );

                const destroyOldOfferLetterAssignments = (
                  slotRule.template_set_attributes
                    .offer_letter_templates_stages_attributes ?? []
                )
                  .filter(a => typeof a.id === 'number')
                  .map(
                    a =>
                      ({
                        id: a.id,
                        _destroy: true,
                      }) as unknown as OfferLetterTemplatesStage,
                  );

                const newOfferLetterAssignments = (
                  contentRule.template_set_attributes
                    .offer_letter_templates_stages_attributes ?? []
                ).map(
                  ({
                    id: _id,
                    template_set_id: _tsId,
                    template_set_type: _tsType,
                    ...rest
                  }) => rest as OfferLetterTemplatesStage,
                );

                const destroyOldConditions = (
                  slotRule.condition_set_attributes.conditions_attributes ?? []
                )
                  .filter(c => typeof c.id === 'number')
                  .map(
                    c => ({ id: c.id, _destroy: true }) as unknown as Condition,
                  );

                const {
                  id: _cId,
                  conditions_attributes: _cAttrs,
                  conditionable_id: _condableId,
                  conditionable_type: _condableType,
                  ref_rule_id: _refRuleId,
                  ref_document_signature_template_rule_id: _refDocSigId,
                  ref_document_signing_template_rule_id: _refDocSigningId,
                  ...condSetRest
                } = contentRule.condition_set_attributes;

                const newConditions = (
                  contentRule.condition_set_attributes.conditions_attributes ??
                  []
                ).map(({ id: _id, ...rest }) => rest as Condition);

                return {
                  id: slotRule.id,
                  template_set_attributes: {
                    id: slotRule.template_set_attributes.id,
                    template_settings_attributes: [
                      ...destroyOldTemplateSettings,
                      ...newTemplateSettings,
                    ],
                    offer_letter_templates_stages_attributes: [
                      ...destroyOldOfferLetterAssignments,
                      ...newOfferLetterAssignments,
                    ],
                  },
                  condition_set_attributes: {
                    ...condSetRest,
                    id: slotRule.condition_set_attributes.id,
                    conditions_attributes: [
                      ...destroyOldConditions,
                      ...newConditions,
                    ],
                  },
                };
              },
            );

            const destroyUserDeleted = userDeletedSavedRules.map(r => ({
              id: r.id,
              _destroy: true as const,
            }));

            // Overflow content sits past every available existing-id slot,
            // all of which are already spoken for by `swappedRules` above
            // (each existing row keeps its id but may now hold different
            // content). So overflow content — whether it originally belonged
            // to a new rule or to an existing row displaced by a rule that
            // took its slot — is (re)created fresh from its own content, in
            // display order, after the swapped slots; freshly created rows
            // get the highest ids and therefore keep their displayed spot at
            // the bottom of the list. All ids (rule, template_set,
            // condition_set, and their children) are stripped rather than
            // just string ones, since a displaced existing rule's content
            // carries real numeric ids that must not collide with the row
            // that `swappedRules` already reassigned them to.
            const newRuleParams = overflowContents.map(ruleAttr => {
              const newTemplateSettings = (
                ruleAttr.template_set_attributes.template_settings_attributes ??
                []
              ).map(
                ({ id: _id, ...rest }) =>
                  rest as DocumentSigningStagesTemplateSetting,
              );

              const {
                id: _cId,
                conditions_attributes: _cAttrs,
                conditionable_id: _condableId,
                conditionable_type: _condableType,
                ref_rule_id: _refRuleId,
                ref_document_signature_template_rule_id: _refDocSigId,
                ref_document_signing_template_rule_id: _refDocSigningId,
                ...condSetRest
              } = ruleAttr.condition_set_attributes;

              const newConditions = (
                ruleAttr.condition_set_attributes.conditions_attributes ?? []
              ).map(({ id: _id, ...rest }) => rest as Condition);

              const newOfferLetterAssignments = (
                ruleAttr.template_set_attributes
                  .offer_letter_templates_stages_attributes ?? []
              ).map(
                ({
                  id: _id,
                  template_set_id: _tsId,
                  template_set_type: _tsType,
                  ...rest
                }) => rest as OfferLetterTemplatesStage,
              );

              return {
                template_set_attributes: {
                  template_settings_attributes: newTemplateSettings,
                  offer_letter_templates_stages_attributes:
                    newOfferLetterAssignments,
                },
                condition_set_attributes: {
                  ...condSetRest,
                  conditions_attributes: newConditions,
                },
              };
            });

            // Destroyed entries go last so the visible rules occupy the same
            // 0..N-1 indices the UI uses to match a backend validation error
            // back to a rule card (DocumentSigningRules.tsx matches on
            // `rules_attributes[N]`, counting only visible rows) — a deleted
            // row earlier in the array would otherwise shift every later
            // rule's payload index out from under its UI index.
            return {
              ...stage.additional_info?.template_rule_set_attributes,
              rules_attributes: [
                ...swappedRules,
                ...newRuleParams,
                ...destroyUserDeleted,
              ] as DocumentSigningStagesRuleAttributes[],
            };
          }
        }

        const transformedRulesAttributes: DocumentSigningStagesRuleAttributes[] =
          [];

        copyRulesAttributes.forEach(ruleAttr => {
          // Strip the frontend-only `position` field (added by transformRules
          // for potential future reordering support) before comparing against
          // or sending to the backend, which has no concept of it for this
          // stage type. Leaving it in causes a false isDirty on load, since
          // the backend-sourced rule_set_attributes never has a position field.
          const { position: _pos, ...ruleAttrRest } = ruleAttr;

          transformedRulesAttributes.push({
            ...ruleAttrRest,
            template_set_attributes: {
              ...ruleAttrRest.template_set_attributes,
              template_settings_attributes:
                ruleAttrRest.template_set_attributes.template_settings_attributes?.map(
                  templateSetting =>
                    transformAttributes(
                      templateSetting,
                      shouldDeleteStringIds,
                    ) as DocumentSigningStagesTemplateSetting,
                ),
            },
            condition_set_attributes: {
              ...ruleAttrRest.condition_set_attributes,
              conditions_attributes:
                ruleAttrRest.condition_set_attributes.conditions_attributes?.map(
                  condition =>
                    transformAttributes(
                      condition,
                      shouldDeleteStringIds,
                    ) as Condition,
                ),
            },
          });
        });

        const undestroyedRules = transformedRulesAttributes.filter(
          rule => !rule._destroy,
        );
        // Only destroy the rule set when the user actually deleted existing
        // rules (destroyed entries are still present in the transformed
        // array). A rule set the server returned with an already-empty
        // rules_attributes must round-trip unchanged, otherwise the injected
        // `_destroy: true` makes the dirty comparison fail on load.
        const shouldDestroyRuleSet =
          transformedRulesAttributes.length > 0 &&
          undestroyedRules.length === 0;

        // Save only: put destroyed entries last so the visible rules occupy
        // the same 0..N-1 indices the UI uses to match a backend validation
        // error back to a rule card (DocumentSigningRules.tsx matches on
        // `rules_attributes[N]`, counting only visible rows) — a deleted row
        // earlier in the array would otherwise shift every later rule's
        // payload index out from under its UI index. The dirty comparison
        // (shouldDeleteStringIds: false) keeps the original order, since its
        // target is the raw server payload.
        const rulesAttributesForPayload = shouldDeleteStringIds
          ? [
              ...undestroyedRules,
              ...transformedRulesAttributes.filter(rule => rule._destroy),
            ]
          : transformedRulesAttributes;

        return {
          ...stage.additional_info?.template_rule_set_attributes,
          ...(shouldDestroyRuleSet && { _destroy: true }),
          rules_attributes: rulesAttributesForPayload,
        };
      }

      if (stage.type === 'PartnerStage') {
        // Sort by the frontend-only `position` (stamped from the server array
        // index in transformRules) so the reconstruction preserves the exact
        // server payload order. Object.values alone returns ascending-id order
        // (JS integer-like key ordering), which differs whenever the backend
        // serializes rules in another order and produces a false isDirty.
        const copyRulesAttributes = Object.values(partnerRules).sort((a, b) => {
          if (a.position !== undefined && b.position !== undefined) {
            return a.position - b.position;
          }
          return 0;
        });

        // Reorder handling — save only. The backend has no `position` column
        // for partner rules and returns them in ascending id order, so a user
        // reorder is persisted by swapping rule content (action + condition)
        // between the existing rule records: after save, id order equals the
        // displayed order while rule/action/condition row identities stay
        // stable where possible (mirrors the RuleStage content-swap above).
        // This must NOT run for the dirty comparison (shouldDeleteStringIds:
        // false): the comparison target is the raw server payload, and
        // swapping there would false-dirty any stage whose payload order
        // differs from id order on load.
        if (shouldDeleteStringIds) {
          const activeRules = copyRulesAttributes.filter(r => !r._destroy);
          const savedActiveRules = activeRules.filter(
            r => typeof r.id === 'number',
          );
          const savedActiveRulesInIdOrder = [...savedActiveRules].sort(
            (a, b) => (a.id as number) - (b.id as number),
          );
          const savedCount = savedActiveRulesInIdOrder.length;

          // The only ids available after save are the `savedCount` existing
          // ids (fixed, ascending) followed by freshly created ids in
          // submission order — new rows always sort after all existing ones.
          // So the desired order round-trips without any swap iff every new
          // (unsaved) rule already sits after every saved rule in the
          // display order, and the saved rules' relative order is untouched.
          const slotContents = activeRules.slice(0, savedCount);
          const overflowContents = activeRules.slice(savedCount);
          const trailingNewRulesOnly = slotContents.every(
            r => typeof r.id === 'number',
          );
          const savedOrderUnchanged = savedActiveRules.every(
            (rule, idx) => rule.id === savedActiveRulesInIdOrder[idx].id,
          );

          if (!trailingNewRulesOnly || !savedOrderUnchanged) {
            const userDeletedSavedRules = copyRulesAttributes.filter(
              r => r._destroy && typeof r.id === 'number',
            );

            // Content-swap the `savedCount` existing ids onto the
            // position-ordered content destined for those slots — that
            // content may itself come from a new (unsaved) rule the user
            // dragged above an existing one, in which case the existing
            // row's identity is kept but its content becomes the new rule's.
            const swappedRules = savedActiveRulesInIdOrder.map(
              (slotRule, i) => {
                const contentRule = slotContents[i];

                return {
                  id: slotRule.id,
                  action_attributes: swapPartnerRuleChild(
                    slotRule.action_attributes,
                    contentRule.action_attributes,
                  ) as Action,
                  condition_attributes: swapPartnerRuleChild(
                    slotRule.condition_attributes,
                    contentRule.condition_attributes,
                  ) as Condition,
                };
              },
            );

            const destroyUserDeleted = userDeletedSavedRules.map(r => ({
              id: r.id,
              _destroy: true,
            }));

            // Overflow content sits past every available existing-id slot,
            // all of which are already spoken for by `swappedRules` above
            // (each existing row keeps its id but may now hold different
            // content). So overflow content — whether it originally belonged
            // to a new rule or to an existing row displaced by a rule that
            // took its slot — is (re)created fresh from its own content, in
            // display order, after the swapped slots; freshly created rows
            // get the highest ids and therefore keep their displayed spot at
            // the bottom of the list. This is not a second write to the
            // displaced row's original id — that id was already reassigned
            // to different content by `swappedRules`.
            const newRuleParams = overflowContents.map(ruleAttr => ({
              action_attributes: swapPartnerRuleChild(
                undefined,
                ruleAttr.action_attributes,
              ) as Action,
              condition_attributes: swapPartnerRuleChild(
                undefined,
                ruleAttr.condition_attributes,
              ) as Condition,
            }));

            return {
              ...stage.additional_info?.rule_set_attributes,
              rules_attributes: [
                ...destroyUserDeleted,
                ...swappedRules,
                ...newRuleParams,
              ] as PartnerRuleAttributes[],
            };
          }
        }

        const transformedRulesAttributes: PartnerRuleAttributes[] = [];

        copyRulesAttributes.forEach(partnerRuleAttr => {
          // Strip the frontend-only `position` field (added by transformRules;
          // partner rules have no reordering feature) before comparing against
          // or sending to the backend, which has no concept of it for this
          // stage type. Leaving it in causes a false isDirty on load, since
          // the backend-sourced rule_set_attributes never has a position field.
          const { position: _pos, ...partnerRuleAttrRest } = partnerRuleAttr;

          transformedRulesAttributes.push({
            ...partnerRuleAttrRest,
            action_attributes: transformAttributes(
              partnerRuleAttrRest.action_attributes,
              shouldDeleteStringIds,
            ) as Action,
            condition_attributes: transformAttributes(
              partnerRuleAttrRest.condition_attributes,
              shouldDeleteStringIds,
            ) as Condition,
          });
        });

        const undestroyedRules = transformedRulesAttributes.filter(
          rule => !rule._destroy,
        );
        // Only destroy the rule set when the user actually deleted existing
        // rules (destroyed entries are still present in the transformed
        // array). A rule set the server returned with an already-empty
        // rules_attributes must round-trip unchanged, otherwise the injected
        // `_destroy: true` makes the dirty comparison fail on load.
        const shouldDestroyRuleSet =
          transformedRulesAttributes.length > 0 &&
          undestroyedRules.length === 0;

        return {
          ...stage.additional_info?.rule_set_attributes,
          ...(shouldDestroyRuleSet && { _destroy: true }),
          rules_attributes: transformedRulesAttributes,
        };
      }
      return {
        rules_attributes: [],
      };
    },
    [stage, rules, documentSigningRules, partnerRules],
  );

  const transformQuestions = (questions: VideoRecordingStageQuestion[]) => {
    return questions.map(question => {
      if (typeof question.id === 'string') {
        return {
          body: question.body,
          max_length: question.max_length,
          _destroy: question._destroy,
        };
      }
      return question;
    });
  };

  // Whenever updateStageResult.data is present, we want to start comparing
  // against that instead of stageResult.data
  const currentStageResult =
    updateStageResult.status === 'ready' &&
    updateStageResult.data.external_id === stage.external_id
      ? updateStageResult
      : stageResult.status === 'ready'
        ? stageResult
        : null;

  const onSaveChanges = () => {
    if (stageResult.status !== 'ready') {
      return;
    }

    // Defense in depth: the Save button is already disabled via `canSave` (see StageHeader),
    // but guard the actual submit here too so nothing can reach WX with an invalid
    // configuration regardless of what UI path triggered onSaveChanges (AUT-537).
    if (!canSave) {
      return;
    }

    const ruleSetKey =
      stage.type === 'DocumentSignatureStage' ||
      stage.type === 'DocumentSigningStage'
        ? 'template_rule_set_attributes'
        : 'rule_set_attributes';

    const stageDataUpdateParams: StageUpdateParams = {
      ...stage,
      move_rule_attributes: stage.move_rule,
      ...(stage.landing_template && {
        landing_template_attributes: stage.landing_template,
      }),
      ...(stage.landing_acknowledgement_template && {
        landing_acknowledgement_template_attributes:
          stage.landing_acknowledgement_template,
      }),
      ...(stage.confirmation_template && {
        confirmation_template_attributes: stage.confirmation_template,
      }),
      ...(stage.cancellation_template && {
        cancellation_template_attributes: stage.cancellation_template,
      }),
      ...(stage.availability_template && {
        availability_template_attributes: stage.availability_template,
      }),
      ...(stage.reason_settings?.length && {
        reason_settings_attributes: generateSettingsAttributes(
          stage.reason_settings,
          currentStageResult?.data.reason_settings,
        ),
      }),
      ...(stage.reminder_settings?.length && {
        reminder_settings_attributes: generateSettingsAttributes(
          stage.reminder_settings,
          currentStageResult?.data.reminder_settings,
        ),
      }),
      ...(stage.trigger_settings?.length
        ? {
            trigger_settings_attributes: generateSettingsAttributes(
              stage.trigger_settings,
              currentStageResult?.data.trigger_settings,
            ),
          }
        : {}),
      ...(stage.type === 'ReviewStage' && {
        hiring_manager_instructions:
          stage.additional_info.hiring_manager_instructions,
        // when there has been a delete action its far easier to remap the positions
        // here right before sending to the BE vs trying to manage it through the immer
        // state and account for create's etc that may of occured before/after the deletes
        data_fields_attributes: (() => {
          let position = 0;
          return stage.additional_info.review_questionnaire.map(dataField => {
            // Match backend behavior and only assign positions to non-destroyed items
            if (!dataField._destroy) {
              position += 1;
              return { ...dataField, position };
            }
            return dataField;
          });
        })(),
      }),
      ...(stage.type === 'TechCheckStage' && {
        customer_support_attributes: stage.additional_info.customer_support,
        settings_attributes: stage.additional_info.settings.map(
          setting => transformAttributes(setting) as TechCheckStageSetting,
        ),
      }),
      ...(stage.type === 'SchedulerV2Stage' &&
        stage.additional_info.session_detail && {
          session_detail_attributes: {
            ...stage.additional_info.session_detail,
            calendar_scheduler_stages_session_details_attributes:
              stage.additional_info.session_detail.calendar_scheduler_stages_session_details?.map(
                calendarSchedulerStagesSessionDetail => ({
                  _destroy: calendarSchedulerStagesSessionDetail._destroy,
                  available: calendarSchedulerStagesSessionDetail.available,
                  calendar_id: calendarSchedulerStagesSessionDetail.calendar_id,
                  id: calendarSchedulerStagesSessionDetail.id,
                }),
              ),
          },
        }),
      // The extra and additional_info can contain the same attributes so order matters.
      // We'll likely need to refactor this on the backend + frontend
      ...stage.extra,
      // Bogus typecast here. W4 stages don't have `additional_info` so we're doing some hand-waving
      ...((stage as WorkflowCustomStage).additional_info ?? {}),
      ...stage.advanced_settings,
      ...(stage.type === 'SchedulerStage' && {
        restricted_locations_attributes:
          stage.additional_info.restricted_locations_attributes?.map(
            restrictedLocation => ({
              ...restrictedLocation,
              id:
                typeof restrictedLocation.id === 'string' // do not set id if it's a string
                  ? undefined
                  : restrictedLocation.id,
            }),
          ),
      }),
      // Handle Partner, Document Signing, and Regular Rules differently with a dynamic ruleSetKey
      [ruleSetKey]: transformRulesToParams({
        shouldDeleteStringIds: true,
      }),
      ...((stage.type === 'DocumentSignatureStage' ||
        stage.type === 'DocumentSigningStage') &&
        stage.additional_info.else_template_set_attributes && {
          else_template_set_attributes: transformAttributes(
            stage.additional_info.else_template_set_attributes,
          ),
        }),
      ...(stage.type === 'VideoRecordingStage' &&
        stage.additional_info.questions && {
          questions_attributes: transformQuestions(
            stage.additional_info.questions,
          ),
        }),
      ...(stage.type === 'DistributeApplicantsRuleStage' &&
        stage.additional_info.distribute_rules && {
          distribute_rules_attributes:
            stage.additional_info.distribute_rules.map(
              distRule => transformAttributes(distRule, true) as DistributeRule,
            ),
        }),
      ...((stage.type === 'JobSelectorStage' ||
        stage.type === 'JobSwitcherStage') &&
        stage.additional_info.conditions && {
          funnel_choice_rule_attributes: {
            conditions: stage.additional_info.conditions,
          },
        }),
      checkr_geo:
        stage.type === 'BackgroundCheckerStage'
          ? JSON.stringify(stage.additional_info?.checkr_geo) // This is saved as a json string on the BE -_-
          : undefined,
    };

    // Explicitly avoid updating the stage#position value on "Save Changes" submit
    // This causes side effects with the sidebar drag-n-drop reordering which already handles this.
    delete stageDataUpdateParams.position;

    updateStage(opening.slug, externalId, {
      workflow_stage: { stage: stageDataUpdateParams },
    });
  };

  useEffect(() => {
    if (updateStageResult.status !== 'idle') {
      resetUpdateStageResult();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalId]);

  // Check if the form is Dirty
  useEffect(() => {
    if (stageResult.status !== 'ready' || !stage.external_id) {
      return;
    }

    const rulesParams = transformRulesToParams({
      shouldDeleteStringIds: false,
    });

    const isStageDirty = !_isEqual(stage, currentStageResult?.data);

    const defaultRulesAttributes = {
      rules_attributes: [DEFAULT_RULE],
    };

    let ruleSetAttributes;

    if (
      stage.type === 'DocumentSigningStage' ||
      stage.type === 'DocumentSignatureStage'
    ) {
      ruleSetAttributes = stage.additional_info.template_rule_set_attributes;
    } else if (
      stage.type === 'RuleStage' ||
      stage.type === 'CustomStage' ||
      stage.type === 'PartnerStage' ||
      stage.type === 'AssessmentStage' ||
      stage.type === 'ComplianceStage' ||
      stage.type === 'RehireCheckStage'
    ) {
      ruleSetAttributes = stage.additional_info.rule_set_attributes;
    }

    const isRulesDirty =
      ruleSetAttributes || rulesParams.rules_attributes.length > 0
        ? !_isEqual(ruleSetAttributes ?? defaultRulesAttributes, rulesParams)
        : false;

    setIsStageTitleDirty(stage.title !== currentStageResult?.data.title);
    setIsDirty(isStageDirty || isRulesDirty);
  }, [currentStageResult?.data, stage, stageResult, transformRulesToParams]);

  // Reset Error Messages if reverting back to clean initial state
  useEffect(() => {
    if (updateStageResult.isError && !isDirty) {
      resetUpdateStageResult();
    }
  }, [updateStageResult, resetUpdateStageResult, isDirty]);

  // Initialize Stage Data
  useEffect(() => {
    if (stageResult.status !== 'ready') {
      return;
    }

    setStageAndTransformationStates(stageResult.data);
  }, [stageResult]);

  // Re-initialize Stage Data after update
  useEffect(() => {
    if (updateStageResult.status !== 'ready') {
      return;
    }

    setStageAndTransformationStates(updateStageResult.data);
  }, [updateStageResult]);

  useEffect(() => {
    if (stageResult.isError && stageResult?.error === 'Forbidden') {
      dispatch(
        addMessageAction(intl.formatMessage(messages.forbiddenError), 'error'),
      );
      history.replace(`/${accountSlug}/openings`);
    } else if (stageResult.isError) {
      dispatch(
        addMessageAction(intl.formatMessage(messages.apiError), 'error', false),
      );
    }
  }, [dispatch, intl, stageResult, history, accountSlug]);

  // Refresh the current stage when the copilot modifies it
  useEffect(() => {
    return addWorkflowInvalidatedListener(() => {
      refetchStage();
    });
  }, [refetchStage]);

  const hasStagesData =
    stageResult.status === 'ready' || stageResult.status === 'reloading';

  return (
    <>
      {withSidebar && (
        <StagesSidebar
          isStageAdditionBlocked={isDirty}
          onDiscardChanges={onDiscardChanges}
          opening={props.opening}
          refetchStages={props.refetchStages}
          selectedStage={props.selectedStage}
          stages={funnelStages}
          unsupportedAxStages={props.unsupportedAxStages}
        />
      )}

      {stageResult.isError && null}

      {stageResult.status === 'loading' && <Loader fullScreen size="2rem" />}

      {hasStagesData && (
        <StageContext.Provider
          value={{
            isDirty,
            isStageTitleDirty,
            funnelStages,
            onDiscardChanges,
            onSaveChanges,
            partnerRules,
            documentSigningRules,
            refetchStage,
            rules,
            setIsDirty,
            setRules,
            setPartnerRules,
            setDocumentSigningRules,
            setStage,
            stage,
            updateStageResult,
            resetUpdateStageResult,
            canSave,
            setCanSave,
          }}
        >
          {children}
          {stageResult.status === 'reloading' && (
            <Loader fullScreen size="2rem" />
          )}
          <UnsavedChangesPrompt
            key={stage.external_id}
            isMultiStage={stage.type === 'MultiStage'}
            isNavigationBlocked={isDirty}
          />
        </StageContext.Provider>
      )}
    </>
  );
};
